/**
 * The server side of sync over one user's replica (sync.md §5–§6, §9–§10): pure functions over a
 * driver. The HTTP routes (packages/server, WP5) add auth, the per-user mutex, plan and byte checks,
 * `devices.last_sync_at`, `users.replica_bytes` and metering around them.
 *
 * The replica keeps one `sync_seq` entry per register (its latest sequence number). A push assigns
 * fresh numbers to every register it writes; a server-side engine write (MCP, push ingest) leaves
 * dirty registers that `sequenceLocalChanges` turns into sequence numbers, so devices receive server
 * writes exactly like device writes.
 */

import type { EpochMs } from '../contracts/ids.js';
import type { SqlDriver, SqlValue } from '../contracts/storage.js';
import {
  SCHEMA_VERSION, SYNC_TABLES, type PullResponse, type PushRequest, type PushResponse, type RegisterChange, type RowChange,
  type SyncTableName, type SyncTableSpec,
} from '../contracts/sync.js';
import type { ReplicaSync } from '../contracts/wiring.js';
import { DigesterError } from '../kernel/errors.js';
import {
  assignSeq, countDirty, dropDirtyRegister, hasSeqAfter, headSeq, keyFromWireId, maxSeqOfPurgeCandidates, oldestDirty,
  selectSyncedRow, seqEntriesAfter, seqsOfRow,
} from '../storage/ext/sync/index.js';
import { childWireIds } from '../storage/cascade.js';
import { TABLE_META } from '../storage/meta.js';
import { getSyncMeta, setSyncMeta } from '../storage/sync-meta.js';
import { mergeChanges } from './apply.js';
import { registerOf } from './registers.js';

const TABLE_ORDER: ReadonlyMap<string, number> = new Map((SYNC_TABLES as readonly SyncTableSpec[]).map((s, i) => [s.table, i]));

/** How many dirty registers `sequenceLocalChanges` reads per statement. */
const SEQUENCE_PAGE = 1000;

function requireEpoch(db: SqlDriver): string {
  const epoch = getSyncMeta(db, 'epoch');
  if (!epoch) throw new DigesterError('internal', 'This replica has no epoch; call replicaSync.ensureEpoch when creating it');
  return epoch;
}

/** Raise the persisted server clock to at least `hlc` (receive rule), so later server writes win. */
function receiveIntoMeta(db: SqlDriver, hlc: string | null): void {
  if (hlc === null) return;
  const current = getSyncMeta(db, 'hlc');
  if (current === null || hlc.slice(0, 18) > current.slice(0, 18)) setSyncMeta(db, 'hlc', hlc);
}

function sequenceDirty(db: SqlDriver): number {
  let n = 0;
  for (;;) {
    const batch = oldestDirty(db, SEQUENCE_PAGE);
    if (batch.length === 0) return n;
    for (const reg of batch) {
      const meta = TABLE_META.get(reg.tbl);
      const key = meta ? keyFromWireId(db, meta, reg.id) : null;
      if (meta && key && selectSyncedRow(db, meta, key)) {
        assignSeq(db, reg.tbl, reg.id, reg.grp);
        n++;
      }
    }
    // Every row read is now sequenced (or gone): drop exactly those queue entries.
    for (const reg of batch) dropDirtyRegister(db, reg);
  }
}

export const replicaSync: ReplicaSync = {
  ensureEpoch(db, randomHex) {
    const existing = getSyncMeta(db, 'epoch');
    if (existing) return existing;
    if (!/^[0-9a-f]{8,64}$/.test(randomHex)) throw new DigesterError('internal', 'ensureEpoch needs 8–64 lower-case hex characters');
    setSyncMeta(db, 'epoch', randomHex);
    return randomHex;
  },

  applyPush(db, request, options) {
    if (typeof request !== 'object' || request === null || !Array.isArray((request as Partial<PushRequest>).changes)) {
      throw new DigesterError('invalid_input', 'A push needs { schemaVersion, changes[] }');
    }
    if (request.schemaVersion !== SCHEMA_VERSION) {
      throw new DigesterError('sync_conflict', `This replica speaks schema ${SCHEMA_VERSION}; the device sent ${String(request.schemaVersion)}. Update the app.`, {
        expected: SCHEMA_VERSION,
      });
    }
    const epoch = requireEpoch(db);
    return db.transaction((): PushResponse => {
      // Anything a server-side engine left unsequenced goes first, so it orders before this push.
      sequenceDirty(db);
      const outcome = mergeChanges(db, request.changes, {
        now: options.now,
        maxClockSkewMs: options.maxClockSkewMs,
        allowServerOwnedTables: false,
        onWritten: (tbl, id, grp) => {
          assignSeq(db, tbl, id, grp);
        },
      });
      receiveIntoMeta(db, outcome.maxHlc);
      // Children of a message that is a stub (or unknown) here are dropped and counted stale (§5.5).
      return { applied: outcome.applied, stale: outcome.stale + outcome.orphans.length, rejected: outcome.rejected, head: headSeq(db), epoch };
    });
  },

  collectPull(db, request) {
    const since = Number(request.since);
    const limit = Number(request.limit);
    if (!Number.isInteger(since) || since < 0 || !Number.isInteger(limit) || limit < 1) {
      throw new DigesterError('invalid_input', 'pull needs integer since >= 0 and limit >= 1');
    }
    const epoch = requireEpoch(db);
    // A pull must never miss a committed server write: sequence what a server engine left dirty.
    if (countDirty(db) > 0) db.transaction(() => sequenceDirty(db));
    const horizon = Number(getSyncMeta(db, 'purged_before_seq') ?? '0');
    // since = 0 means "I have nothing": a full pull is complete whatever was purged (sync.md §9).
    if (since > 0 && since < horizon) {
      throw new DigesterError('resync_required', 'This device has been away longer than deleted items are kept; it must download everything again.', {
        horizon,
      });
    }
    const entries = seqEntriesAfter(db, since, limit);
    const next = entries.length > 0 ? (entries[entries.length - 1] as { seq: number }).seq : since;
    const more = hasSeqAfter(db, next);

    // Rows touched by this page, in first-seq order.
    const rows = new Map<string, { tbl: string; id: string }>();
    for (const e of entries) {
      const k = `${e.tbl}\u0000${e.id}`;
      if (!rows.has(k)) rows.set(k, { tbl: e.tbl, id: e.id });
    }

    const out = new Map<string, RowChange>();
    /**
     * Every register of the row with seq > since (so registers written together travel together,
     * even across a page boundary); ALL registers when none has seq <= since — the device may never
     * have seen the row (sync.md §6 rule 4) — or when `whole`. Returns the stored row, or undefined.
     */
    const addRow = (tbl: string, id: string, whole = false): Record<string, SqlValue> | undefined => {
      const meta = TABLE_META.get(tbl);
      const key = meta ? keyFromWireId(db, meta, id) : null;
      if (!meta || !key) return undefined;
      const stored = selectSyncedRow(db, meta, key);
      if (!stored) return undefined;
      const k = `${tbl}\u0000${id}`;
      const existing = out.get(k);
      if (existing && (!whole || Object.keys(existing.regs).length === meta.registers.length)) return stored;
      const seqs = seqsOfRow(db, tbl, id);
      const all = whole || [...seqs.values()].every((s) => s > since);
      const regs: Record<string, RegisterChange> = {};
      for (const spec of meta.registers) {
        const s = seqs.get(spec.name);
        if (all || (s !== undefined && s > since)) {
          const reg = registerOf(meta, stored, spec.name);
          if (reg) regs[spec.name] = reg;
        }
      }
      if (Object.keys(regs).length > 0) out.set(k, { t: tbl as SyncTableName, id, regs });
      return stored;
    };

    for (const { tbl, id } of rows.values()) {
      const stored = addRow(tbl, id);
      const meta = TABLE_META.get(tbl);
      if (!stored || !meta?.parentMessageColumn) continue;
      // A child row brings along whatever of its parent message the device may not have seen yet
      // (the whole row if it may be unknown), so its copy of the parent is current when it applies it.
      addRow('messages', String(stored[meta.parentMessageColumn]));
    }

    // Child-set rule: a message whose life register travels brings ALL its current child rows, whole.
    // The device makes its children of that message equal to this set (plus its unpushed ones). The
    // stub cascade deletes children physically, which LWW cannot carry: a device whose delete lost to
    // a concurrent restore, or that never saw a stub because a restore replaced it, holds a different
    // child set; this is where it converges (sync/index.ts reconcileChildren).
    for (const change of [...out.values()]) {
      if (change.t !== 'messages' || change.regs.life === undefined) continue;
      for (const [table, ids] of Object.entries(childWireIds(db, change.id))) {
        for (const childId of ids) addRow(table, childId, true);
      }
    }

    const changes = [...out.values()].sort((a, b) => (TABLE_ORDER.get(a.t) ?? 0) - (TABLE_ORDER.get(b.t) ?? 0));
    const response: PullResponse = { changes, next, more, head: headSeq(db), epoch };
    return response;
  },

  sequenceLocalChanges(db) {
    if (countDirty(db) === 0) return 0;
    return db.transaction(() => sequenceDirty(db));
  },
};

/**
 * Run a tombstone purge on a replica and move the resync horizon (sync.md §9): devices whose cursor is
 * below the highest sequence number the purge may remove get `resync_required`. `purge` is the
 * engine's purge (WP3), run inside the same transaction; `cutoff` is its
 * `now − retention.tombstone_ttl_days`. Returns what `purge` returned.
 */
export function purgeWithHorizon<T>(db: SqlDriver, cutoff: EpochMs, purge: () => T): T {
  return db.transaction(() => {
    const candidate = maxSeqOfPurgeCandidates(db, cutoff);
    const result = purge();
    applyPurgeHorizon(db, candidate);
    return result;
  });
}

/**
 * Raise `sync_meta.purged_before_seq` to `seq` (never lowers it) and re-sequence every surviving
 * register at or below it, in their order, above the head. Without that, a device doing a paged full
 * pull from 0 would ask for `since` values below the horizon and be told to resync forever; with it,
 * no `next` the server hands out can fall below the horizon, so only a cursor from before the purge
 * can. Devices re-pull the moved registers once (stale no-ops). Returns how many were moved.
 */
export function applyPurgeHorizon(db: SqlDriver, seq: number): number {
  const current = Number(getSyncMeta(db, 'purged_before_seq') ?? '0');
  if (seq <= current) return 0;
  return db.transaction(() => {
    setSyncMeta(db, 'purged_before_seq', String(seq));
    let moved = 0;
    for (;;) {
      const batch = seqEntriesAfter(db, 0, SEQUENCE_PAGE).filter((e) => e.seq <= seq);
      if (batch.length === 0) return moved;
      for (const e of batch) assignSeq(db, e.tbl, e.id, e.grp);
      moved += batch.length;
    }
  });
}
